import { Injectable } from '@angular/core'
import { concat, forkJoin, Observable, of, throwError } from 'rxjs'
import { catchError, map, publishLast, refCount, switchMap, tap } from 'rxjs/operators'
import { CacheEntry } from 'src/utils/cache-entry'
import { CrudAction, CrudSubject } from 'src/utils/crud-subject'
import { Http404Error } from 'src/utils/errors'
import { Arn } from 'src/utils/types'
import { ResourceType } from 'src/utils/resource-types'
import { ApiService } from 'src/app/services/api.service'
import {
  AwsTargetResourceDto,
  ProcessingStatusDto,
  RecoveryPointAttemptDto,
  RecoveryPointDto,
  ResourceConfigDto,
  SyncPairDto,
  SyncPairIssueDto,
  SyncPairIssueScopeDto,
  SyncPairResourceConfigDto,
} from 'src/app/services/dtos'
import { maxDate, processingCompleteTime, processingIsActive } from 'src/app/shared/utils'
import { isPersistentIssue } from './issue'
import { ObservableCache } from 'src/utils/rxjs-utils'

// tslint:disable:variable-name
export class SyncPair {
  static STAGING_POLL_INTERVAL = 15000
  static SYNC_POLL_INTERVAL = 5000

  static publishSubject$ = new CrudSubject<SyncPair>()

  private _targetResources: Array<AwsTargetResourceDto> = null
  private _issues: Array<SyncPairIssueDto> = null
  private _syncCacheTimestamp: Date = null
  private _stagingRequestTimestamp: Date = null
  private _syncRequestTimestamp: Date = null
  private _failbackRequestTimestamp: Date = null
  private _recoveryPoints: Array<RecoveryPointDto> = null
  private _recoveryPointAttempts: Array<RecoveryPointAttemptDto> = null
  private _manualResourceTemplateUrl$: Observable<string> = null

  constructor(
    private _apiService: ApiService,
    public accountId: string,
    private _syncPairDto: SyncPairDto
  ) {}

  static load$(
    apiService: ApiService,
    accountId: string,
    syncPairDto: SyncPairDto,
    applicationRestoreStatus: ProcessingStatusDto
  ) {
    const newSyncPair = new SyncPair(apiService, accountId, syncPairDto)
    return newSyncPair.load$(applicationRestoreStatus)
  }

  static loadSyncPair$(
    apiService: ApiService,
    accountId: string,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    applicationRestoreStatus: ProcessingStatusDto
  ) {
    return apiService
      .getSyncPair(accountId, sourceEndpointUri, targetEndpointUri)
      .pipe(
        switchMap((syncPairDto: SyncPairDto) =>
          SyncPair.load$(apiService, accountId, syncPairDto, applicationRestoreStatus)
        )
      )
  }

  static buildUniqueId(sourceEndpointUri: string, targetEndpointUri: string) {
    return `${sourceEndpointUri}|${targetEndpointUri}`
  }

  get uniqueId() {
    return SyncPair.buildUniqueId(this.sourceEndpointUri, this.targetEndpointUri)
  }

  /////////////////////////////////// BACKUP TIMESTAMPS /////////////////////////////////////

  private get stagingStatus() {
    return this._syncPairDto.stagingStatus
  }

  get currentlyBackingUp() {
    return this.lastBackupPerceivedStart > this.lastBackupComplete
  }

  get lastBackupRequest(): Date {
    // overlay the most recent request timestamp on what's in the DTO, to paper-over
    // the delay between requesting a new staging and it actually starting.
    return maxDate(this.stagingStatus && this.stagingStatus.lastRequest, this._stagingRequestTimestamp)
  }

  get lastBackupStart(): Date {
    return this.stagingStatus && this.stagingStatus.lastStart
  }

  get lastBackupPerceivedStart(): Date {
    return maxDate(this.lastBackupStart, this.lastBackupRequest)
  }

  get lastBackupSuccess() {
    return this.stagingStatus && this.stagingStatus.lastSuccess
  }

  get lastBackupFailure() {
    return this.stagingStatus && this.stagingStatus.lastFailure
  }

  get lastBackupComplete() {
    return maxDate(this.lastBackupFailure, this.lastBackupSuccess)
  }

  /////////////////////////////////// RESTORE TIMESTAMPS /////////////////////////////////////

  private get syncStatus() {
    return this._syncPairDto.syncStatus
  }

  get currentlyRestoring() {
    return this.lastRestorePerceivedStart > this.lastRestoreComplete
  }

  get lastRestoreRequest() {
    // overlay the last sync request timestamp on top of the DTO timestamp to paper-over the
    // brief delay between requesting a sync and it actually starting.
    return maxDate(this.syncStatus && this.syncStatus.lastRequest, this._syncRequestTimestamp)
  }

  get lastRestoreStart() {
    return this.syncStatus && this.syncStatus.lastStart
  }

  get lastRestorePerceivedStart(): Date {
    return maxDate(this.lastRestoreStart, this.lastRestoreRequest)
  }

  get lastRestoreSuccess() {
    return this.syncStatus && this.syncStatus.lastSuccess
  }

  get lastRestoreFailure() {
    return this.syncStatus && this.syncStatus.lastFailure
  }

  get lastRestoreComplete() {
    return maxDate(this.lastRestoreFailure, this.lastRestoreSuccess)
  }

  /////////////////////////////////// FAILBACK TIMESTAMPS /////////////////////////////////////

  private get failbackStatus() {
    return this._syncPairDto.failbackStatus
  }

  get currentlyFailingBack() {
    return this.failbackStatus && this.lastFailbackPerceivedStart > this.lastFailbackComplete
  }

  get lastFailbackRequest() {
    return maxDate(this.failbackStatus && this.failbackStatus.lastRequest, this._failbackRequestTimestamp)
  }

  get lastFailbackStart() {
    return this.failbackStatus && this.failbackStatus.lastStart
  }

  get lastFailbackPerceivedStart() {
    return maxDate(this.lastFailbackStart, this.lastFailbackRequest)
  }

  get lastFailbackSuccess() {
    return this.failbackStatus && this.failbackStatus.lastSuccess
  }

  get lastFailbackFailure() {
    return this.failbackStatus && this.failbackStatus.lastFailure
  }

  get lastFailbackComplete() {
    return maxDate(this.lastFailbackSuccess, this.lastFailbackFailure)
  }

  get sourceEndpointUri() {
    return this._syncPairDto.sourceEndpointUri
  }

  get sourceAwsAccountId() {
    return this._syncPairDto.sourceAwsAccountId
  }

  get sourceRegion() {
    return this._syncPairDto.sourceRegion
  }

  get targetEndpointUri() {
    return this._syncPairDto.targetEndpointUri
  }

  get targetAwsAccountId() {
    return this._syncPairDto.targetAwsAccountId
  }

  get targetRegion() {
    return this._syncPairDto.targetRegion
  }

  get recoveryPoints() {
    return this._recoveryPoints
  }

  get recoveryPointAttempts() {
    return this._recoveryPointAttempts
  }

  private _buildRefreshObservable$(applicationRestoreStatus: ProcessingStatusDto, forceRefresh: boolean = false) {
    /**
     * Creates an observable that will refresh the SyncPair from the server, and then loads
     * resource status, issues, and AwsTargetResources as necessary
     */

    return this._apiService.getSyncPair(this.accountId, this.sourceEndpointUri, this.targetEndpointUri).pipe(
      switchMap((syncPairDto: SyncPairDto) => {
        // Update the DTO here.  We access properties of this in following code, and they rely on the updated state of the DTO.
        this._syncPairDto = syncPairDto

        // default the observables to be of the current values from prior API calls.
        let targetResources$ = of(this._targetResources)

        // If applicationBackupStatus or applicationRestoreStatus were passed in as non-null, then we must be a multi-account where the
        // backup and restore are managed at the application level.  If they're null, we're non-multi-account and we need to build
        // them from the info on the syncPairDto.
        const applicationRestoreActive = processingIsActive(applicationRestoreStatus)

        // After a process completes, we need to poll 1 more time to get the final resource status and issues
        // This also comes into play when we switch back to an application/sync pair after a while and new
        // processing has occurred.
        const needsFinalRestorePoll =
          (applicationRestoreStatus ? processingCompleteTime(applicationRestoreStatus) : this.lastRestoreComplete) >
          this._syncCacheTimestamp

        // This block is the same logic, but for sync instead of staging.
        const syncRefreshNeeded =
          needsFinalRestorePoll || // cached data out-of-date
          applicationRestoreActive // currently restoring

        // if sync needs refresh, also update the target resource mappings.
        if (syncRefreshNeeded || forceRefresh) {
          targetResources$ = this._apiService.listTargetResources(
            this.accountId,
            this.sourceEndpointUri,
            this.targetEndpointUri
          )
        }

        // launch these updates in parallel.  Using the map version of forkJoin because we're over 6 observables and
        // the array format craps out at 6 (apparently a limitation in the rxjs type definitions used with Typescript)
        return forkJoin({
          syncPairDto: of(syncPairDto),
          targetResources: targetResources$,
        })
      }),
      map(({ syncPairDto: syncPairDto, targetResources: targetResources }) => {
        // update cached resources, issues, status, recoveryPoints, and recovery point attempts,
        // and return the SyncPair, now fully-populated
        this._targetResources = targetResources
        this._syncCacheTimestamp = maxDate(this.lastRestoreStart, this.lastBackupComplete)

        return this
      })
    )
  }

  load$(applicationRestoreStatus: ProcessingStatusDto) {
    // Populate this sync pair, but don't poll if it's currently processing.  This is called from the context where
    // we're initially loading all sync pairs, and we don't necessarily need to be refreshing this one.
    return this._buildRefreshObservable$(applicationRestoreStatus, false).pipe(
      tap((syncPair) => SyncPair.publishSubject$.upsert(syncPair, syncPair.uniqueId))
    )
  }

  // TODO: Do we need to include the failback status in the arguments here?
  refreshSyncPair$(
    applicationBackupStatus: ProcessingStatusDto,
    applicationRestoreStatus: ProcessingStatusDto,
    applicationFailbackStatus: ProcessingStatusDto
  ) {
    /**
     * Builds an observable that refreshes the SyncPair from the server, and then
     * refreshes resources and issues if they are out-of-date.  If a staging or sync
     * is underway, it sleeps then polls, emitting the latest SyncPair data with
     * each cycle, until no staging or syncing is ongoing.
     */

    return new Observable<SyncPair>((subscriber) => {
      let timeout = null
      let subscription = null
      const that = this
      let performFinalPoll = false

      function refresh() {
        timeout = null
        subscription = that._buildRefreshObservable$(applicationRestoreStatus, false).subscribe(
          (syncPair) => {
            subscriber.next(syncPair)
            SyncPair.publishSubject$.upsert(syncPair, syncPair.uniqueId)

            if (
              syncPair.currentlyBackingUp ||
              syncPair.currentlyRestoring ||
              syncPair.currentlyFailingBack ||
              performFinalPoll
            ) {
              // tslint:disable-next-line:no-shadowed-variable
              let delay: number
              if (syncPair.currentlyBackingUp && syncPair.currentlyRestoring) {
                delay = Math.min(SyncPair.STAGING_POLL_INTERVAL, SyncPair.SYNC_POLL_INTERVAL)
              } else if (syncPair.currentlyBackingUp) {
                delay = SyncPair.STAGING_POLL_INTERVAL
              } else if (syncPair.currentlyRestoring) {
                delay = SyncPair.SYNC_POLL_INTERVAL
              } else if (syncPair.currentlyFailingBack) {
                delay = SyncPair.SYNC_POLL_INTERVAL
              } else if (performFinalPoll) {
                // we aren't staging or syncing currently, so this must be the final poll
                // that is intended to paper-over the transition from staging to syncing.
                // We just poll once - if the sync hasn't started yet at that time, it's
                // probably because an application is in test/failover and the sync won't
                // run.
                delay = SyncPair.SYNC_POLL_INTERVAL
              }
              // Clear the performFinalPoll flag if we are no longer staging.
              performFinalPoll =
                syncPair.currentlyBackingUp || syncPair.currentlyRestoring || syncPair.currentlyFailingBack

              timeout = window.setTimeout(refresh, delay)
              subscription = null
            } else {
              // For multi-account, where the processing status isn't on the sync pair, we should always fall into this block the very
              // first time.  As such, this will never recursively call refresh() and we'll just get one update of the sync pari
              // (which is what we want).
              subscriber.complete()
              subscription = null
            }
          },
          (error) => subscriber.error(error)
        )
      }

      // Invoke the initial refresh.
      refresh()

      // Return a subscription.
      return {
        unsubscribe: () => {
          if (!!subscription) {
            subscription.unsubscribe()
          }
          if (timeout) {
            window.clearTimeout(timeout)
          }
        },
      }
    })
  }

  get targetResources(): Array<AwsTargetResourceDto> {
    return this._targetResources || []
  }

  targetResourceMap() {
    if (this._targetResources) {
      return new Map<Arn, AwsTargetResourceDto>(
        this._targetResources.map((res) => [res.sourceArn, res] as [string, AwsTargetResourceDto])
      )
    } else {
      return new Map()
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Issue Accessors
  ////////////////////////////////////////////////////////////////////////////////
  get issueDtos() {
    function issueNewerThan(issueDto: SyncPairIssueDto, processStartDate: Date) {
      // validate that an issue is not out of date
      return issueDto.timestamp >= processStartDate || isPersistentIssue(issueDto.issue)
    }

    function isIssueScope(issueDto: SyncPairIssueDto, issueScopes: Array<SyncPairIssueScopeDto>) {
      // helper for checking issue scope
      return issueScopes.find((scope) => scope === issueDto.scope) !== undefined
    }

    return (
      this._issues &&
      this._issues.filter((issueDto) => {
        return (
          (isIssueScope(issueDto, [SyncPairIssueScopeDto.STAGING, SyncPairIssueScopeDto.CLEANUP]) &&
            issueNewerThan(issueDto, this.lastBackupPerceivedStart)) ||
          (isIssueScope(issueDto, [SyncPairIssueScopeDto.SYNC]) &&
            issueNewerThan(issueDto, this.lastRestorePerceivedStart)) ||
          (isIssueScope(issueDto, [SyncPairIssueScopeDto.FAILBACK]) && issueNewerThan(issueDto, this.lastFailbackStart))
        )
      })
    )
  }
  get manualResourceTemplateUrl$() {
    if (!this._manualResourceTemplateUrl$) {
      // We want to cache the result of this so that multiple people asking for the same
      // thing in parallel won't launch a bunch of identical requests.  This happens when
      // many IAM entities couldn't be updated for a single sync pair.
      // Adding publishLast() will give us this caching behavior.
      this._manualResourceTemplateUrl$ = this._apiService
        .getManualResourceTemplate(
          this.accountId,
          this.sourceAwsAccountId,
          this.sourceRegion,
          this.targetAwsAccountId,
          this.targetRegion
        )
        .pipe(
          map((manualResourceTemplateDto) => {
            return manualResourceTemplateDto.templateS3Url
          })
        )
        .pipe(publishLast(), refCount())
    }
    return this._manualResourceTemplateUrl$
  }

  getSyncPairResourceConfig$(arn: string) {
    return this._apiService
      .getSyncPairResourceConfig(this.accountId, this.sourceEndpointUri, this.targetEndpointUri, arn)
      .pipe(
        map(adaptSyncPairResourceConfig),
        catchError((err) => {
          // eat a 404 and return null
          return err instanceof Http404Error ? of(null) : throwError(err)
        })
      )
  }

  setSyncPairResourceConfig$(arn: string, resourceConfigDto: ResourceConfigDto) {
    return this._apiService
      .setSyncPairResourceConfig(this.accountId, this.sourceEndpointUri, this.targetEndpointUri, arn, resourceConfigDto)
      .pipe(map(adaptSyncPairResourceConfig))
  }

  deleteSyncPairIssue$(issueId: string, scope: SyncPairIssueScopeDto) {
    return this._apiService
      .deleteSyncPairIssue(this.accountId, this.sourceEndpointUri, this.targetEndpointUri, scope.toString(), issueId)
      .pipe(
        map((response) => {
          this._issues = this._issues.filter((issue) => issue.issueId !== issueId)
          SyncPair.publishSubject$.upsert(this, this.uniqueId)
          return response
        })
      )
  }
}

export interface ResourceConfig {
  type: ResourceType
}

export enum S3ObjectReplicationMode {
  none = 'none',
  realTime = 'realTime',
  snapshot = 'snapshot',
}

export class S3BucketConfig implements ResourceConfig {
  type = ResourceType.s3Bucket
  backfillDays: number
  customerManagesReplication: boolean
  objectReplicationMode: S3ObjectReplicationMode
  recoveryBucketSuffix?: string

  constructor(dto: ResourceConfigDto) {
    this.backfillDays = dto.backfillDays
    this.customerManagesReplication = dto.customerManagesReplication
    this.objectReplicationMode = dto.objectReplicationMode
    this.recoveryBucketSuffix = dto.recoveryBucketSuffix
  }
}

export class AzureBlobContainerConfig implements ResourceConfig {
  static resourceConfigType = 'azureBlobContainer'
  type = ResourceType.azureBlobContainer
  backfillDays: number
  objectReplicationMode: S3ObjectReplicationMode

  constructor(dto: ResourceConfigDto) {
    this.backfillDays = dto.backfillDays
    this.objectReplicationMode = dto.objectReplicationMode
  }
}

export class Ec2InstanceConfig implements ResourceConfig {
  static resourceConfigType = 'ec2Instance'
  realTimeRpoEnabled: boolean
  quarantined: boolean
  scan: boolean
  type = ResourceType.ec2Instance

  constructor(dto: ResourceConfigDto) {
    this.realTimeRpoEnabled = dto.realTimeRpoEnabled
    this.quarantined = dto.quarantined
  }
}

export function resourceConfigDtoToResourceConfig(dto: ResourceConfigDto): ResourceConfig {
  switch (dto.resourceConfigType) {
    case ResourceType.s3Bucket.valueOf():
      return new S3BucketConfig(dto)
    case Ec2InstanceConfig.resourceConfigType:
      return new Ec2InstanceConfig(dto)
    case AzureBlobContainerConfig.resourceConfigType:
      return new AzureBlobContainerConfig(dto)
  }
  throw new Error(`Unknown resource config type: ${dto.resourceConfigType}`)
}

export class SyncPairResourceConfig {
  arn: string
  substituteArn?: string
  resourceConfig: ResourceConfig

  constructor(dto: SyncPairResourceConfigDto) {
    this.arn = dto.arn
    this.substituteArn = dto.substituteArn
    this.resourceConfig = resourceConfigDtoToResourceConfig(dto.resourceConfig)
  }
}

function adaptSyncPairResourceConfig(configDto: SyncPairResourceConfigDto) {
  return new SyncPairResourceConfig(configDto)
}

@Injectable({
  providedIn: 'root',
})
export class SyncPairProviderService {
  private syncPairCache: Map<string, CacheEntry<SyncPair>> = null
  private accountId: string = null

  constructor(private apiService: ApiService) {
    SyncPair.publishSubject$.subscribe((crudEvent) => {
      if (
        crudEvent.action === CrudAction.UPSERT &&
        crudEvent.entity.accountId === this.accountId &&
        !!this.syncPairCache
      ) {
        this.syncPairCache.set(crudEvent.entityId, new CacheEntry(crudEvent.entity))
      } else if (!!this.syncPairCache) {
        this.syncPairCache.delete(crudEvent.entityId)
      }
    })
  }
  private observableCache = new ObservableCache<SyncPair>()

  loadSyncPair$(
    accountId: string,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    applicationBackupStatus: ProcessingStatusDto,
    applicationRestoreStatus: ProcessingStatusDto,
    applicationFailbackStatus: ProcessingStatusDto
  ) {
    const uniqueId = SyncPair.buildUniqueId(sourceEndpointUri, targetEndpointUri)
    let observable: Observable<SyncPair>

    if (!!this.syncPairCache && this.syncPairCache.has(uniqueId)) {
      const syncPair = this.syncPairCache.get(uniqueId)
      if (syncPair.entity.accountId !== accountId) {
        throw new Error('The sync pair cache contains sync pairs from the wrong account ID.')
      }
      observable = concat(
        of<SyncPair>(syncPair.entity),
        syncPair.entity.refreshSyncPair$(applicationBackupStatus, applicationRestoreStatus, applicationFailbackStatus)
      )
    } else {
      observable = SyncPair.loadSyncPair$(
        this.apiService,
        accountId,
        sourceEndpointUri,
        targetEndpointUri,
        applicationRestoreStatus
      )
    }

    // We want to cache this observable so that multiple applications on the same sync pair will share
    // a single stream of updates.
    return this.observableCache.cache(uniqueId, observable)
  }
}
